import React, { useState } from 'react';
import { User, Mail, Phone, Car, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const ProfileSettings = ({ customerData, applications, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    name: customerData?.name || '',
    email: customerData?.email || '',
    phone: customerData?.phone || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage(null);

      // Update all applications with the new customer data
      const updatePromises = applications?.map(app => 
        supabase?.from('applications')?.update({
            customer_name: formData?.name,
            customer_email: formData?.email,
            customer_phone: formData?.phone
          })?.eq('id', app?.id)
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const hasErrors = results?.some(result => result?.error);
      
      if (hasErrors) {
        throw new Error('Fehler beim Aktualisieren der Daten');
      }

      setMessage({ type: 'success', text: 'Profil erfolgreich aktualisiert!' });
      setIsEditing(false);
      
      // Reload customer data
      if (onUpdate) {
        await onUpdate();
      }
      
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'error', text: 'Fehler beim Speichern. Bitte versuchen Sie es erneut.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: customerData?.name || '',
      email: customerData?.email || '',
      phone: customerData?.phone || ''
    });
    setIsEditing(false);
    setMessage(null);
  };

  return (
    <div className="space-y-6">
      {/* Profile Information Card */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Mein Profil</h2>
                <p className="text-blue-100 text-sm">Verwalten Sie Ihre persönlichen Daten</p>
              </div>
            </div>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Bearbeiten
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="bg-white text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-green-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Speichert...' : 'Speichern'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 ${message?.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
            <div className="flex items-center space-x-3">
              {message?.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <p className={`text-sm font-medium ${message?.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {message?.text}
              </p>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="p-6 space-y-6">
          {/* Name Field */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 mr-2" />
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200' :'border-gray-200 bg-gray-50 text-gray-600'
              }`}
              placeholder="Ihr vollständiger Name"
            />
          </div>

          {/* Email Field */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 mr-2" />
              E-Mail-Adresse
            </label>
            <input
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200' :'border-gray-200 bg-gray-50 text-gray-600'
              }`}
              placeholder="ihre.email@beispiel.de"
            />
          </div>

          {/* Phone Field */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 mr-2" />
              Telefonnummer
            </label>
            <input
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200' :'border-gray-200 bg-gray-50 text-gray-600'
              }`}
              placeholder="+49 123 456789"
            />
          </div>

          {/* Information Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">Hinweis zur Datenänderung</p>
                <p className="text-sm text-blue-800">
                  Änderungen an Ihren Kontaktdaten werden auf alle Ihre bestehenden Anträge angewendet. 
                  Dies stellt sicher, dass Sie über alle wichtigen Updates informiert werden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle History Card */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Car className="w-6 h-6 text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">Fahrzeughistorie</h3>
          </div>
        </div>
        <div className="p-6">
          {applications && applications?.length > 0 ? (
            <div className="space-y-4">
              {applications?.map((app, index) => (
                <div key={app?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Car className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{app?.vehicle_model}</p>
                      {app?.chassis_number && (
                        <p className="text-sm text-gray-600">FIN: {app?.chassis_number}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Antragsnummer</p>
                    <p className="font-mono text-xs text-gray-500">{app?.id?.slice(0, 8)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Keine Fahrzeuge registriert</p>
            </div>
          )}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Datenschutz & Sicherheit</h4>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Ihre Daten werden verschlüsselt und sicher gespeichert</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Wir geben Ihre Daten nicht an Dritte weiter</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Sie können Ihre Daten jederzeit ändern oder löschen</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Weitere Informationen finden Sie in unserer Datenschutzerklärung</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfileSettings;